/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * IncomingCallOverlay.tsx — Incomin\1 \2al\1 \2verlay (services/operator-web-app).
 */

import { useEffect, useState, useCallback } from 'react';
import { useCall, useOperatorActions } from '@/store';
import { buildCallAccept, buildCallReject } from '@/realtime/commands';
import { useTranslation } from '@/i18n';

/**
 * Full-screen overlay shown when a call is offered (phase === 'offered').
 * Shows caller info, auto-accept countdown, and accept/decline buttons.
 * Pressing Enter accepts the call.
 */
export function IncomingCallOverlay() {
  const t = useTranslation();
  const call = useCall();
  const { send } = useOperatorActions();
  const [secondsLeft, setSecondsLeft] = useState<number>(15);

  const isOffered = call.phase === 'offered';
  const offer = call.offer;

  /** Accepts the current offer if it still has a call identifier. */
  const handleAccept = useCallback(() => {
    if (!call.callId) return;
    send(buildCallAccept(call.callId));
  }, [call.callId, send]);

  /** Declines the current offer with an operator-owned reason code. */
  const handleDecline = useCallback(() => {
    if (!call.callId) return;
    send(buildCallReject(call.callId, 'operator_declined'));
  }, [call.callId, send]);

  // Reset countdown when a new offer arrives
  useEffect(() => {
    if (!isOffered || !offer) return;
    const total = Math.ceil((offer.timeoutMs ?? 15000) / 1000);
    setSecondsLeft(total);
  }, [isOffered, offer]);

  // Countdown timer
  useEffect(() => {
    if (!isOffered) return;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          // Auto-accept when countdown reaches 0
          handleAccept();
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [handleAccept, isOffered]);

  // Keyboard: Enter to accept
  useEffect(() => {
    if (!isOffered) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAccept();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOffered, handleAccept]);

  if (!isOffered || !offer) return null;

  const progressPercent = offer.timeoutMs
    ? Math.max(0, (secondsLeft / Math.ceil(offer.timeoutMs / 1000)) * 100)
    : 0;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      role="alertdialog"
      aria-label={t('call.incoming')}
    >
      <div
        className="glass-panel"
        style={{
          width: '100%',
          maxWidth: 420,
          padding: 'var(--space-2xl)',
          textAlign: 'center',
        }}
      >
        {/* Pulsing phone icon */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 'var(--space-lg)',
          }}
        >
          <div className="relative">
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(34, 197, 94, 0.15)',
                border: '2px solid rgba(34, 197, 94, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <div
                className="animate-ping"
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background: 'rgba(34, 197, 94, 0.1)',
                }}
              />
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#22c55e"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Heading */}
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-xl)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-xs)',
          }}
        >
          {t('call.incoming')}
        </h2>

        {/* Caller info */}
        <p
          style={{
            fontSize: 'var(--text-lg)',
            color: 'var(--text-primary)',
            fontWeight: 500,
            marginBottom: 'var(--space-xs)',
            fontFamily: 'var(--font-display)',
          }}
        >
          {offer.from}
        </p>

        {/* Queue info */}
        {offer.queueId && (
          <p
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--text-muted)',
              marginBottom: 'var(--space-lg)',
            }}
          >
            {t('call.queue', { queue: offer.queueId })}
          </p>
        )}

        {/* Countdown progress */}
        <div
          style={{
            marginBottom: 'var(--space-lg)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-xs)',
          }}
        >
          <div
            style={{
              width: '100%',
              height: 4,
              borderRadius: 2,
              background: 'var(--glass-border)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progressPercent}%`,
                background: '#22c55e',
                borderRadius: 2,
                transition: 'width 1s linear',
              }}
            />
          </div>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
            {t('call.auto_answer', { seconds: secondsLeft })}
          </span>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3" style={{ justifyContent: 'center' }}>
          <button
            className="glass-button"
            onClick={handleAccept}
            style={{
              flex: 1,
              justifyContent: 'center',
              fontWeight: 600,
              background: '#22c55e',
              borderColor: '#22c55e',
              color: '#fff',
            }}
          >
            {t('call.accept_short')}
          </button>
          <button
            className="glass-button"
            onClick={handleDecline}
            style={{
              flex: 1,
              justifyContent: 'center',
              fontWeight: 600,
              background: 'rgba(239, 68, 68, 0.15)',
              borderColor: 'rgba(239, 68, 68, 0.5)',
              color: '#ef4444',
            }}
          >
            {t('call.reject_short')}
          </button>
        </div>

        {/* Keyboard hint */}
        <p
          style={{
            marginTop: 'var(--space-md)',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-muted)',
          }}
        >
          {t('call.enter_accept')}
        </p>
      </div>
    </div>
  );
}
